import { AppConsts } from '@shared/commonHelper/AppConsts';

import { LOST_REASONS, LostReasonCode } from './lost-reasons';

/**
 * Configurable lost reasons (LEAD_LOST_REASONS_PLAN.md; story LEAD_LOST_REASONS_STORY.md): the one list every "Mark lost" picker reads
 * (rule 17), and the pure rules the pickers share - 6 + "{N} more reasons" (rule 20), the Other note (rule 21), the hint (rule 19),
 * the stage default (rules 26-27) and what a pick sends to the server.
 *
 * A reason is addressed by a `key`: its SrcLostReasons id as text while the feature is on, MD's code while it is off (the two never
 * collide). `LostReasonCatalogService` holds the tenant's list; everything here is pure so the harness specs run it.
 */

/** One reason a picker offers. */
export interface LostReasonOption {
    /** The id as text (feature on), or MD's code (feature off). */
    readonly key: string;
    /** SrcLostReasons.Id; null for MD's six. */
    readonly id: number | null;
    /** MD's code for the six (feature off); null for a configured reason (the server dual-writes the code itself). */
    readonly code: LostReasonCode | null;
    readonly name: string;
    /** A lucide icon name registered in MainModule (the catalogue's keys are registered under the same names). */
    readonly icon: string;
    /** Rule 21: picking it reveals the optional "Add a note" field. */
    readonly takesNote: boolean;
}

/** The tenant's list and its rules, as one immutable snapshot. */
export interface LostReasonCatalog {
    /** Configurable lost reasons are on (and loaded); false = MD's six codes. */
    readonly enabled: boolean;
    readonly options: readonly LostReasonOption[];
    /** Rule 11: Skip is hidden and a reason must be chosen. Never true while the feature is off. */
    readonly requireReason: boolean;
    /** Rules 26-27: a Lost stage's default reason key, by stage id (live reasons only). */
    readonly stageDefaults: ReadonlyMap<number, string>;
    /** Rule 20: reasons shown before "{N} more reasons". */
    readonly visibleCount: number;
    readonly maxReasons: number;
    readonly maxNameLength: number;
    readonly maxNoteLength: number;
}

/** The parts of `LostReasonsServiceProxy.getLostReasonSettings` the catalogue reads (LostReasonSettingsDto satisfies it). */
export interface LostReasonSettingsLike {
    reasons?: { id?: number; name?: string; icon?: string; sortOrder?: number; takesNote?: boolean }[];
    requireReason?: boolean;
    stageDefaults?: { stageId?: number; lostReasonId?: number }[];
    maxReasons?: number;
    maxNameLength?: number;
    maxNoteLength?: number;
    pickerVisibleCount?: number;
}

export const LOST_REASON_VISIBLE_COUNT = 6;
export const LOST_REASON_MAX_REASONS = 12;
export const LOST_REASON_MAX_NAME = 40;
export const LOST_REASON_MAX_NOTE = 120;

/**
 * The copy every picker shares. Q1 (user, 2026-10-22: "Store only, no Leo"): the design's hints without the AI's name, as LP and MD
 * already word them.
 */
export const LOST_REASON_COPY = Object.freeze({
    why: 'Why?',
    group: 'Lost reason',
    hintIdle: 'Optional — helps show why leads drop off.',
    hintPicked: 'Reason saved.',
    hintRequired: 'Required — choose a reason to continue.',
    /** The Mark-lost picker's header line when a reason is required and none is picked (C1164). */
    gridRequired: 'Required · choose a reason to continue',
    showLess: 'Show less',
    notePlaceholder: 'Add a note (optional)',
    noteLabel: 'Note for Other',
    noReason: 'No reason recorded',
    removed: '(removed)',
    footnote: 'Helps show where and why leads drop off.',
});

/** `{n} more reason` / `{n} more reasons` (C3462). */
export function moreReasonsLabel(hidden: number): string {
    return hidden === 1 ? '1 more reason' : `${hidden} more reasons`;
}

/** The feature is on: its own flag AND the Lead Pipeline's (plan §3). */
export function lostReasonsEnabled(): boolean {
    return !!AppConsts.lostReasonsFeatureEnabled && !!AppConsts.leadPipelineFeatureEnabled;
}

/** MD's six, as a catalogue: the feature off, or on before the tenant's list has loaded. */
export function legacyLostReasonCatalog(): LostReasonCatalog {
    return {
        enabled: false,
        options: LOST_REASONS.map((reason): LostReasonOption => ({
            key: reason.code,
            id: null,
            code: reason.code,
            name: reason.label,
            icon: reason.icon,
            takesNote: false,
        })),
        requireReason: false,
        stageDefaults: new Map<number, string>(),
        visibleCount: LOST_REASON_VISIBLE_COUNT,
        maxReasons: LOST_REASON_MAX_REASONS,
        maxNameLength: LOST_REASON_MAX_NAME,
        maxNoteLength: LOST_REASON_MAX_NOTE,
    };
}

/** The tenant's list from the settings read, in its settings order (rule 17). */
export function catalogFromSettings(dto: LostReasonSettingsLike | null | undefined): LostReasonCatalog {
    const reasons = ((dto && dto.reasons) || [])
        .filter((r) => !!r && typeof r.id === 'number' && !!r.name)
        .slice()
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || (a.id as number) - (b.id as number));
    const options = reasons.map((r): LostReasonOption => ({
        key: String(r.id),
        id: r.id as number,
        code: null,
        name: r.name as string,
        icon: r.icon || 'circle-x',
        takesNote: !!r.takesNote,
    }));
    const live = new Set(options.map((o) => o.key));
    const stageDefaults = new Map<number, string>();
    ((dto && dto.stageDefaults) || []).forEach((d) => {
        if (d && typeof d.stageId === 'number' && typeof d.lostReasonId === 'number' && live.has(String(d.lostReasonId))) {
            stageDefaults.set(d.stageId, String(d.lostReasonId));
        }
    });
    return {
        enabled: true,
        options,
        requireReason: !!(dto && dto.requireReason),
        stageDefaults,
        visibleCount: (dto && dto.pickerVisibleCount) || LOST_REASON_VISIBLE_COUNT,
        maxReasons: (dto && dto.maxReasons) || LOST_REASON_MAX_REASONS,
        maxNameLength: (dto && dto.maxNameLength) || LOST_REASON_MAX_NAME,
        maxNoteLength: (dto && dto.maxNoteLength) || LOST_REASON_MAX_NOTE,
    };
}

export function findLostReason(catalog: LostReasonCatalog, key: string | null | undefined): LostReasonOption | null {
    if (key === null || key === undefined || key === '') {
        return null;
    }
    return catalog.options.find((o) => o.key === key) || null;
}

/** The key a picker shows for a stored reason: its id while the feature is on, its code while off; null when it is not offered. */
export function lostReasonKeyOf(catalog: LostReasonCatalog, id: number | null | undefined, code: string | null | undefined): string | null {
    const byId = typeof id === 'number' ? findLostReason(catalog, String(id)) : null;
    if (byId) {
        return byId.key;
    }
    const byCode = code ? catalog.options.find((o) => o.code === code) : null;
    return byCode ? byCode.key : null;
}

/** Rules 26-27: the reason a picker pre-selects for a Lost stage (null = "No default · ask each time"). */
export function stageDefaultKey(catalog: LostReasonCatalog, stageId: number | null | undefined): string | null {
    if (typeof stageId !== 'number') {
        return null;
    }
    const key = catalog.stageDefaults.get(stageId);
    return key && findLostReason(catalog, key) ? key : null;
}

/** Rule 21: the note field shows for the picked reason. */
export function lostReasonTakesNote(catalog: LostReasonCatalog, key: string | null | undefined): boolean {
    const option = findLostReason(catalog, key);
    return !!option && option.takesNote;
}

/** The toggle under the pills or the grid (rule 20). */
export interface LostReasonToggleView {
    readonly label: string;
    /** The list is open ("Show less", grey, chevron-up); else "{N} more reasons" (blue, chevron-down). */
    readonly open: boolean;
}

export interface LostReasonListView {
    readonly visible: readonly LostReasonOption[];
    /** Null with 6 or fewer reasons, and while a hidden reason is picked (the list stays open, rule 20). */
    readonly toggle: LostReasonToggleView | null;
}

/**
 * Rule 20 (3a/3b, 4e/4f; C3452-3462, T9180-9188): the first `visibleCount` reasons, or all of them when the user opened the list or
 * the picked reason is one of the hidden ones - and then no toggle, so the pick stays visible.
 */
export function buildLostReasonList(
    options: readonly LostReasonOption[],
    pickedKey: string | null | undefined,
    userOpen: boolean,
    visibleCount: number = LOST_REASON_VISIBLE_COUNT
): LostReasonListView {
    const count = options.length;
    if (count <= visibleCount) {
        return { visible: options, toggle: null };
    }
    const pickedAt = pickedKey ? options.findIndex((o) => o.key === pickedKey) : -1;
    const forced = pickedAt >= visibleCount;
    const open = forced || userOpen;
    return {
        visible: open ? options : options.slice(0, visibleCount),
        toggle: forced ? null : { label: open ? LOST_REASON_COPY.showLess : moreReasonsLabel(count - visibleCount), open },
    };
}

/** Rule 19 (T9176): the hint beside "Why?". */
export function lostReasonHintText(pickedKey: string | null | undefined, required: boolean): string {
    if (pickedKey) {
        return LOST_REASON_COPY.hintPicked;
    }
    return required ? LOST_REASON_COPY.hintRequired : LOST_REASON_COPY.hintIdle;
}

/** What a pick sends to a write endpoint: an id (+ the Other note) while the feature is on, MD's code while off. */
export interface LostReasonRequest {
    readonly lostReasonId: number | undefined;
    readonly lostReason: string | undefined;
    readonly lostReasonNote: string | undefined;
}

export const NO_LOST_REASON: LostReasonRequest = Object.freeze({
    lostReasonId: undefined,
    lostReason: undefined,
    lostReasonNote: undefined,
});

export function lostReasonRequest(
    catalog: LostReasonCatalog,
    key: string | null | undefined,
    note: string | null | undefined
): LostReasonRequest {
    const option = findLostReason(catalog, key);
    if (!option) {
        return NO_LOST_REASON;
    }
    if (option.id === null) {
        return { lostReasonId: undefined, lostReason: option.code || undefined, lostReasonNote: undefined };
    }
    const trimmed = option.takesNote ? (note || '').trim().slice(0, catalog.maxNoteLength) : '';
    return { lostReasonId: option.id, lostReason: undefined, lostReasonNote: trimmed ? trimmed : undefined };
}

/**
 * A stored reason as of the CURRENT list (rules 7, 23): a rename or a delete in Settings shows on a Lost card / banner at once,
 * without re-reading it. Until the list has loaded (or with the feature off) the server's name and flag stand.
 */
export function liveLostReason(
    catalog: LostReasonCatalog | null | undefined,
    id: number | null | undefined,
    name: string | null | undefined,
    removed: boolean | null | undefined
): { readonly name: string | null; readonly removed: boolean } {
    if (!catalog || !catalog.enabled || typeof id !== 'number') {
        return { name: name || null, removed: !!removed };
    }
    const option = findLostReason(catalog, String(id));
    return option ? { name: option.name, removed: false } : { name: name || null, removed: true };
}

/** The reason line of a Lost card / banner (rules 7, 9, 21): the current name, "(removed)" when deleted, " — {note}". */
export interface LostReasonDisplay {
    readonly text: string;
    /** Rule 7: the reason was deleted - drawn grey. */
    readonly removed: boolean;
    readonly hasReason: boolean;
}

export function lostReasonDisplay(
    name: string | null | undefined,
    removed: boolean | null | undefined,
    note: string | null | undefined,
    fallbackLabel: string | null | undefined
): LostReasonDisplay {
    const label = (name || fallbackLabel || '').trim();
    if (!label) {
        return { text: LOST_REASON_COPY.noReason, removed: false, hasReason: false };
    }
    const named = removed ? `${label} ${LOST_REASON_COPY.removed}` : label;
    const trimmedNote = (note || '').trim();
    return { text: trimmedNote ? `${named} — ${trimmedNote}` : named, removed: !!removed, hasReason: true };
}

/**
 * A create form's `Why?` block (story entry point E8; plan decision 7): shown under a Lost stage while the feature is on, for
 * any client type. `stageId` is the stage the pick belongs to, so picking another Lost stage pre-picks that stage's default.
 */
export interface LostReasonDraft {
    readonly shown: boolean;
    readonly stageId: number | null;
    readonly reason: string | null;
    readonly note: string;
}

export const NO_LOST_REASON_DRAFT: LostReasonDraft = Object.freeze({ shown: false, stageId: null, reason: null, note: '' });

/**
 * The draft for the form's stage now: hidden off a Lost stage (and while the feature is off); a newly chosen Lost stage
 * pre-picks its default reason (rules 26-27); the same stage keeps the user's pick.
 */
export function lostReasonDraftFor(
    draft: LostReasonDraft,
    catalog: LostReasonCatalog,
    stageId: number | null | undefined,
    isLostStage: boolean
): LostReasonDraft {
    if (!catalog.enabled || typeof stageId !== 'number' || !isLostStage) {
        return NO_LOST_REASON_DRAFT;
    }
    if (draft.shown && draft.stageId === stageId) {
        return draft;
    }
    return { shown: true, stageId, reason: stageDefaultKey(catalog, stageId), note: '' };
}

/** A reason picked (or cleared) in the block; a key the catalogue does not offer clears it. */
export function pickLostReasonDraft(draft: LostReasonDraft, catalog: LostReasonCatalog, key: string | null | undefined): LostReasonDraft {
    if (!draft.shown) {
        return draft;
    }
    const reason = findLostReason(catalog, key) ? (key as string) : null;
    return reason === draft.reason ? draft : { ...draft, reason };
}

/** Rule 21: the Other note, as typed. */
export function noteLostReasonDraft(draft: LostReasonDraft, note: string | null | undefined): LostReasonDraft {
    const value = typeof note === 'string' ? note : '';
    return !draft.shown || value === draft.note ? draft : { ...draft, note: value };
}

/** Rule 11: the block is up with no reason while the tenant requires one - the create waits. */
export function lostReasonDraftBlocked(draft: LostReasonDraft, catalog: LostReasonCatalog): boolean {
    return draft.shown && catalog.enabled && catalog.requireReason && !findLostReason(catalog, draft.reason);
}
